import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import { useApi } from '../auth/useApi';
import { UserAccessPopover } from '../components/UserAccessPopover';

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

const PRIORITY_COLORS: Record<string, string> = {
  Critical: '#c4533a',
  High: '#c4793a',
  Medium: '#7a9b7e',
  Low: '#a8a8a8',
};

interface Me {
  userId: number;
  email: string;
  displayName: string;
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

interface Ticket {
  TicketId: number;
  TicketKey?: string;
  Subject: string;
  Priority: string;
  StatusName: string;
  StatusId: number;
  CategoryName: string | null;
  CreatedByName: string;
  AssignedToName: string | null;
  CreatedByUserId: number;
  AssignedToUserId: number | null;
  SecondaryNames?: string | null;
  SecondaryUserIds?: string | null;
  CreatedAt: string;
  UpdatedAt?: string;
  ResolvedAt: string | null;
}

const fmtRelative = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const Badge: React.FC<{ children: React.ReactNode; color: string }> = ({ children, color }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    color, background: `${color}15`, border: `1px solid ${color}40`,
  }}>{children}</span>
);

export const TicketsList: React.FC<{ me: Me }> = ({ me }) => {
  const api = useApi();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [statuses, setStatuses] = useState<{ StatusId: number; Name: string; IsTerminal: boolean }[]>([]);
  const [techs, setTechs] = useState<{ UserId: number; DisplayName: string; Email: string; Role: string; Active: boolean }[]>([]);
  const [categories, setCategories] = useState<{ CategoryId: number; Name: string }[]>([]);
  // Which dropdown is open in the bulk bar
  const [bulkAction, setBulkAction] = useState<'status' | 'assign' | 'category' | null>(null);
  const [bulkPendingStatus, setBulkPendingStatus] = useState<{ StatusId: number; Name: string; IsTerminal: boolean } | null>(null);
  const [bulkResolutionText, setBulkResolutionText] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  // Mass merge modal
  const [bulkMergeOpen, setBulkMergeOpen] = useState(false);
  // Admin + tech can bulk-operate. Status changes still require admin (server enforces).
  const canBulk = me.role === 'admin' || me.role === 'tech';
  const canBulkStatus = me.role === 'admin';

  // Filters
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  // Initial status: priority order is (1) URL ?status= param (deep links from Dashboard),
  // (2) last choice from localStorage, (3) sensible default per role (open for tech/admin, all for user).
  const initialStatus = (() => {
    const fromUrl = searchParams.get('status');
    if (fromUrl === 'open' || fromUrl === 'closed' || fromUrl === 'all') return fromUrl;
    try {
      const saved = localStorage.getItem('hp.ticketsList.statusFilter');
      if (saved === 'open' || saved === 'closed' || saved === 'all') return saved;
    } catch { /* ignore (private browsing) */ }
    // Sensible default per role
    if (me.role === 'admin' || me.role === 'tech') return 'open';
    return 'all';
  })();
  const [statusFilter, setStatusFilterState] = useState<string>(initialStatus);
  const setStatusFilter = (v: string) => {
    setStatusFilterState(v);
    if (v === 'all') searchParams.delete('status');
    else searchParams.set('status', v);
    setSearchParams(searchParams, { replace: true });
    // Remember this choice so it sticks across sessions
    try { localStorage.setItem('hp.ticketsList.statusFilter', v); } catch { /* ignore */ }
  };
  // Priority filter — URL-backed (?priority=) so dashboard charts can deep-link
  const [priorityFilter, setPriorityFilterState] = useState<string>(searchParams.get('priority') || 'all');
  const setPriorityFilter = (v: string) => {
    setPriorityFilterState(v);
    if (v === 'all') searchParams.delete('priority'); else searchParams.set('priority', v);
    setSearchParams(searchParams, { replace: true });
  };
  // Category filter — URL-backed (?category=). 'Uncategorized' matches tickets with no category.
  const [categoryFilter, setCategoryFilterState] = useState<string>(searchParams.get('category') || 'all');
  const setCategoryFilter = (v: string) => {
    setCategoryFilterState(v);
    if (v === 'all') searchParams.delete('category'); else searchParams.set('category', v);
    setSearchParams(searchParams, { replace: true });
  };
  // Assignee (tech) + requester filters — URL-backed, matched by display name so
  // dashboard charts grouped by tech/requester can deep-link here.
  const [assigneeFilter, setAssigneeFilterState] = useState<string>(searchParams.get('assignee') || 'all');
  const setAssigneeFilter = (v: string) => {
    setAssigneeFilterState(v);
    if (v === 'all') searchParams.delete('assignee'); else searchParams.set('assignee', v);
    setSearchParams(searchParams, { replace: true });
  };
  const [requesterFilter, setRequesterFilterState] = useState<string>(searchParams.get('requester') || 'all');
  const setRequesterFilter = (v: string) => {
    setRequesterFilterState(v);
    if (v === 'all') searchParams.delete('requester'); else searchParams.set('requester', v);
    setSearchParams(searchParams, { replace: true });
  };
  const [mineOnly, setMineOnly] = useState(false);
  // Sort: 'newest' | 'oldest' | 'priority' (Critical → Low) | 'updated'
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'priority' | 'updated'>(() => {
    try {
      const saved = localStorage.getItem('hp.ticketsList.sortBy');
      if (saved === 'newest' || saved === 'oldest' || saved === 'priority' || saved === 'updated') return saved;
    } catch { /* ignore */ }
    return 'newest';
  });
  const setSortByAndSave = (v: typeof sortBy) => {
    setSortBy(v);
    try { localStorage.setItem('hp.ticketsList.sortBy', v); } catch { /* ignore */ }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, s, u, c] = await Promise.all([
          api.get<Ticket[]>('/api/tickets'),
          api.get<{ StatusId: number; Name: string; IsTerminal: boolean }[]>('/api/admin/statuses').catch(() => []),
          canBulk
            ? api.get<{ UserId: number; DisplayName: string; Email: string; Role: string; Active: boolean }[]>('/api/users').catch(() => [])
            : Promise.resolve([]),
          canBulk
            ? api.get<{ CategoryId: number; Name: string }[]>('/api/categories').catch(() => [])
            : Promise.resolve([]),
        ]);
        if (!cancelled) {
          setTickets(Array.isArray(t) ? t : []);
          setStatuses(Array.isArray(s) ? s : []);
          // Only include admin/tech as assignable, and only active users
          setTechs(Array.isArray(u) ? u.filter(x => x.Active && (x.Role === 'admin' || x.Role === 'tech')) : []);
          setCategories(Array.isArray(c) ? c : []);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Failed to load tickets');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload tickets — used after bulk operations
  const reloadTickets = async () => {
    try {
      const t = await api.get<Ticket[]>('/api/tickets');
      setTickets(Array.isArray(t) ? t : []);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to reload tickets');
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Initiate a bulk status change. If the target is terminal, gather a resolution first.
  const startBulkStatusChange = (status: { StatusId: number; Name: string; IsTerminal: boolean }) => {
    setBulkAction(null);
    if (status.IsTerminal) {
      setBulkPendingStatus(status);
      setBulkResolutionText('');
    } else {
      executeBulkStatusChange(status, undefined);
    }
  };

  const executeBulkStatusChange = async (
    status: { StatusId: number; Name: string; IsTerminal: boolean },
    resolution: string | undefined
  ) => {
    if (selectedIds.size === 0) return;
    setBulkSaving(true);
    try {
      const body: any = {
        ticketIds: Array.from(selectedIds),
        statusId: status.StatusId,
      };
      if (resolution !== undefined) body.resolution = resolution;
      const result = await api.post<{ updatedCount: number; newStatus: string }>('/api/tickets/bulk/status', body);
      setSelectedIds(new Set());
      setBulkPendingStatus(null);
      setBulkResolutionText('');
      await reloadTickets();
      alert(`Updated ${result.updatedCount} ticket(s) to "${result.newStatus}".`);
    } catch (e: any) {
      alert(`Bulk update failed: ${e?.message ?? 'unknown error'}`);
    } finally {
      setBulkSaving(false);
    }
  };

  // Bulk assign tickets to a tech (or unassign with null)
  const executeBulkAssign = async (userId: number | null, displayName: string) => {
    if (selectedIds.size === 0) return;
    setBulkAction(null);
    setBulkSaving(true);
    try {
      const result = await api.post<{ updated: number; assigneeName: string | null }>(
        '/api/tickets/bulk/assign',
        { ticketIds: Array.from(selectedIds), assigneeUserId: userId }
      );
      setSelectedIds(new Set());
      await reloadTickets();
      alert(`Assigned ${result.updated} ticket(s) to ${displayName}.`);
    } catch (e: any) {
      alert(`Bulk assign failed: ${e?.message ?? 'unknown error'}`);
    } finally { setBulkSaving(false); }
  };

  // Bulk set category
  const executeBulkCategory = async (categoryId: number | null, categoryName: string) => {
    if (selectedIds.size === 0) return;
    setBulkAction(null);
    setBulkSaving(true);
    try {
      const result = await api.post<{ updated: number; categoryName: string | null }>(
        '/api/tickets/bulk/category',
        { ticketIds: Array.from(selectedIds), categoryId }
      );
      setSelectedIds(new Set());
      await reloadTickets();
      alert(`Set category to "${categoryName}" on ${result.updated} ticket(s).`);
    } catch (e: any) {
      alert(`Bulk category failed: ${e?.message ?? 'unknown error'}`);
    } finally { setBulkSaving(false); }
  };

  const filtered = useMemo(() => {
    let out = tickets;
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(t =>
        t.Subject.toLowerCase().includes(s) ||
        String(t.TicketId).includes(s) ||
        (t.CreatedByName ?? '').toLowerCase().includes(s)
      );
    }
    if (statusFilter !== 'all') {
      if (statusFilter === 'open') {
        out = out.filter(t => t.StatusName !== 'Resolved' && t.StatusName !== 'Closed');
      } else if (statusFilter === 'closed') {
        out = out.filter(t => t.StatusName === 'Resolved' || t.StatusName === 'Closed');
      } else {
        out = out.filter(t => t.StatusName === statusFilter);
      }
    }
    if (priorityFilter !== 'all') {
      out = out.filter(t => t.Priority === priorityFilter);
    }
    if (categoryFilter !== 'all') {
      if (categoryFilter === 'Uncategorized') {
        out = out.filter(t => !t.CategoryName);
      } else {
        out = out.filter(t => t.CategoryName === categoryFilter);
      }
    }
    if (assigneeFilter !== 'all') {
      if (assigneeFilter === 'Unassigned') out = out.filter(t => !t.AssignedToName);
      else out = out.filter(t => t.AssignedToName === assigneeFilter);
    }
    if (requesterFilter !== 'all') {
      out = out.filter(t => t.CreatedByName === requesterFilter);
    }
    if (mineOnly && me.role === 'tech') {
      out = out.filter(t => t.AssignedToUserId === me.userId || (t.SecondaryUserIds ?? '').split(',').includes(String(me.userId)));
    }
    if (mineOnly && me.role === 'user') {
      out = out.filter(t => t.CreatedByUserId === me.userId);
    }

    // Sort
    const PRIORITY_RANK: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };
    out = [...out];
    if (sortBy === 'newest') {
      out.sort((a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime());
    } else if (sortBy === 'oldest') {
      out.sort((a, b) => new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime());
    } else if (sortBy === 'priority') {
      // Critical → Low, then newest within each priority
      out.sort((a, b) => {
        const pr = (PRIORITY_RANK[a.Priority] ?? 99) - (PRIORITY_RANK[b.Priority] ?? 99);
        if (pr !== 0) return pr;
        return new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime();
      });
    } else if (sortBy === 'updated') {
      out.sort((a, b) => new Date(b.UpdatedAt ?? b.CreatedAt).getTime() - new Date(a.UpdatedAt ?? a.CreatedAt).getTime());
    }
    return out;
  }, [tickets, search, statusFilter, priorityFilter, categoryFilter, assigneeFilter, requesterFilter, mineOnly, me, sortBy]);

  const uniqueStatuses = useMemo(
    () => Array.from(new Set(tickets.map(t => t.StatusName))).sort(),
    [tickets]
  );

  if (loading) return <div style={{ padding: 40, color: C.textMuted }}>Loading tickets…</div>;
  if (error) return <div style={{ padding: 40, color: C.danger }}>Failed to load: {error}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
        <div>
          <h1 style={{ color: C.primary, fontSize: 26, margin: 0 }}>Tickets</h1>
          <p style={{ color: C.textMuted, margin: '4px 0 0' }}>
            {filtered.length} of {tickets.length} {tickets.length === 1 ? 'ticket' : 'tickets'}
          </p>
        </div>
        {me.role !== 'viewer' && (
          <button style={{
            background: C.primary, color: '#fff', border: 'none', padding: '8px 14px',
            borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }} onClick={() => navigate('/tickets/new')}>
            <Plus size={14} /> New Ticket
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: 16, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
      }}>
        <div style={{ flex: '1 1 220px', position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: C.textMuted }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search subject, ID, requester…"
            style={{
              border: `1px solid ${C.border}`, padding: '8px 10px 8px 30px',
              borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box',
              background: C.bg,
            }}
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{
          border: `1px solid ${C.border}`, padding: '8px 10px', borderRadius: 6,
          fontSize: 14, background: C.bg, minWidth: 130,
        }}>
          <option value="all">All Status</option>
          <option value="open">Open only</option>
          <option value="closed">Resolved/Closed</option>
          {(statuses.length ? statuses.map(s => s.Name) : uniqueStatuses).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} style={{
          border: `1px solid ${C.border}`, padding: '8px 10px', borderRadius: 6,
          fontSize: 14, background: C.bg, minWidth: 120,
        }}>
          <option value="all">All Priority</option>
          <option value="Critical">Critical</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{
          border: `1px solid ${C.border}`, padding: '8px 10px', borderRadius: 6,
          fontSize: 14, background: C.bg, minWidth: 140,
        }}>
          <option value="all">All Categories</option>
          <option value="Uncategorized">Uncategorized</option>
          {categories.map(c => <option key={c.CategoryId} value={c.Name}>{c.Name}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortByAndSave(e.target.value as any)} style={{
          border: `1px solid ${C.border}`, padding: '8px 10px', borderRadius: 6,
          fontSize: 14, background: C.bg, minWidth: 150,
        }}>
          <option value="newest">Sort: Newest first</option>
          <option value="oldest">Sort: Oldest first</option>
          <option value="priority">Sort: Priority (Critical→Low)</option>
          <option value="updated">Sort: Recently updated</option>
        </select>
        {(me.role === 'tech' || me.role === 'user') && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={mineOnly} onChange={e => setMineOnly(e.target.checked)} />
            Mine only
          </label>
        )}
      </div>

      {/* Active drill-down chips (assignee / requester come from dashboard charts) */}
      {(assigneeFilter !== 'all' || requesterFilter !== 'all') && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {assigneeFilter !== 'all' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, background: C.bg, color: C.primary, border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 10px' }}>
              Assignee: {assigneeFilter}
              <button onClick={() => setAssigneeFilter('all')} title="Clear" style={{ background: 'transparent', border: 'none', color: C.primary, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>
            </span>
          )}
          {requesterFilter !== 'all' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, background: C.bg, color: C.primary, border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 10px' }}>
              Requester: {requesterFilter}
              <button onClick={() => setRequesterFilter('all')} title="Clear" style={{ background: 'transparent', border: 'none', color: C.primary, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>
            </span>
          )}
        </div>
      )}

      {/* Bulk action bar — appears when rows are selected */}
      {canBulk && selectedIds.size > 0 && (
        <div style={{
          background: C.primary, color: 'white',
          padding: '10px 16px', borderRadius: 6,
          marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>
            {selectedIds.size} ticket{selectedIds.size === 1 ? '' : 's'} selected
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', position: 'relative', flexWrap: 'wrap' }}>
            {/* Change status — admin only */}
            {canBulkStatus && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setBulkAction(bulkAction === 'status' ? null : 'status')}
                  disabled={bulkSaving || statuses.length === 0}
                  style={bulkBtnSty()}
                >Status ▾</button>
                {bulkAction === 'status' && (
                  <BulkMenu onClose={() => setBulkAction(null)}>
                    {statuses.map(s => (
                      <BulkMenuItem key={s.StatusId} onClick={() => startBulkStatusChange(s)}>
                        {s.Name}
                        {s.IsTerminal && <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 6 }}>(needs resolution)</span>}
                      </BulkMenuItem>
                    ))}
                  </BulkMenu>
                )}
              </div>
            )}

            {/* Assign to tech — admin + tech */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setBulkAction(bulkAction === 'assign' ? null : 'assign')}
                disabled={bulkSaving}
                style={bulkBtnSty()}
              >Assign ▾</button>
              {bulkAction === 'assign' && (
                <BulkMenu onClose={() => setBulkAction(null)}>
                  <BulkMenuItem onClick={() => executeBulkAssign(null, 'Unassigned')}>
                    <span style={{ color: C.textMuted, fontStyle: 'italic' }}>Unassign</span>
                  </BulkMenuItem>
                  {techs.map(u => (
                    <BulkMenuItem key={u.UserId} onClick={() => executeBulkAssign(u.UserId, u.DisplayName)}>
                      {u.DisplayName}
                      <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 6 }}>{u.Role}</span>
                    </BulkMenuItem>
                  ))}
                </BulkMenu>
              )}
            </div>

            {/* Set category — admin + tech */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setBulkAction(bulkAction === 'category' ? null : 'category')}
                disabled={bulkSaving || categories.length === 0}
                style={bulkBtnSty()}
              >Category ▾</button>
              {bulkAction === 'category' && (
                <BulkMenu onClose={() => setBulkAction(null)}>
                  <BulkMenuItem onClick={() => executeBulkCategory(null, 'Uncategorized')}>
                    <span style={{ color: C.textMuted, fontStyle: 'italic' }}>Clear category</span>
                  </BulkMenuItem>
                  {categories.map(c => (
                    <BulkMenuItem key={c.CategoryId} onClick={() => executeBulkCategory(c.CategoryId, c.Name)}>
                      {c.Name}
                    </BulkMenuItem>
                  ))}
                </BulkMenu>
              )}
            </div>

            {/* Mass merge — admin + tech */}
            <button
              onClick={() => setBulkMergeOpen(true)}
              disabled={bulkSaving || selectedIds.size < 2}
              title={selectedIds.size < 2 ? 'Select 2+ tickets to merge' : ''}
              style={{ ...bulkBtnSty(), opacity: selectedIds.size < 2 ? 0.5 : 1 }}
            >Merge…</button>

            <button
              onClick={() => setSelectedIds(new Set())}
              style={{
                background: 'transparent', color: 'white',
                border: '1px solid rgba(255,255,255,0.4)', padding: '6px 14px', borderRadius: 4,
                fontSize: 13, cursor: 'pointer',
              }}
            >Clear</button>
          </div>
        </div>
      )}

      {/* Mass merge modal */}
      {bulkMergeOpen && (
        <BulkMergeModal
          ticketIds={Array.from(selectedIds)}
          allTickets={tickets}
          onClose={() => setBulkMergeOpen(false)}
          onMerged={async () => {
            setSelectedIds(new Set());
            setBulkMergeOpen(false);
            await reloadTickets();
          }}
        />
      )}

      {/* Bulk resolution modal — shown when bulk-changing to a terminal status */}
      {bulkPendingStatus && (
        <div
          onClick={() => !bulkSaving && setBulkPendingStatus(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 8, padding: 24,
              width: '90%', maxWidth: 520,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 18, color: C.text }}>
              Bulk close {selectedIds.size} ticket{selectedIds.size === 1 ? '' : 's'} as "{bulkPendingStatus.Name}"
            </h2>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: C.textMuted }}>
              The resolution below will be applied to every selected ticket. Tickets that already have a resolution will be overwritten only if you provide a new one. Minimum 10 characters.
            </p>
            <textarea
              value={bulkResolutionText}
              onChange={e => setBulkResolutionText(e.target.value)}
              placeholder="e.g. Bulk-closed stale tickets after no response from requester for 30+ days."
              rows={5}
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: 10, fontSize: 14, lineHeight: 1.5,
                border: `1px solid ${C.border}`, borderRadius: 4,
                fontFamily: 'inherit', resize: 'vertical',
              }}
            />
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
              {bulkResolutionText.trim().length}/10 minimum
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setBulkPendingStatus(null)}
                disabled={bulkSaving}
                style={{
                  background: 'transparent', border: `1px solid ${C.border}`, color: C.text,
                  padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={() => executeBulkStatusChange(bulkPendingStatus, bulkResolutionText.trim())}
                disabled={bulkSaving || bulkResolutionText.trim().length < 10}
                style={{
                  background: C.primary, color: 'white', border: 'none',
                  padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer', fontWeight: 600,
                  opacity: (bulkSaving || bulkResolutionText.trim().length < 10) ? 0.5 : 1,
                }}
              >
                {bulkSaving ? 'Updating…' : `Close ${selectedIds.size} ticket${selectedIds.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, overflowX: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
            No tickets match the current filters.
          </div>
        ) : (
          <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: C.bg }}>
              <tr>
                {canBulk && (
                  <th style={{ ...th(), width: 32, paddingRight: 0 }}>
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every(t => selectedIds.has(t.TicketId))}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(filtered.map(t => t.TicketId)));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                    />
                  </th>
                )}
                <th style={th()}>#</th>
                <th style={th()}>Subject</th>
                <th style={th()}>Priority</th>
                <th style={th()}>Status</th>
                <th style={th()}>Requester</th>
                <th style={th()}>Assignee</th>
                <th style={th()}>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.TicketId} style={{
                  borderTop: `1px solid ${C.border}`,
                  background: selectedIds.has(t.TicketId) ? '#eaf2ec' : 'transparent',
                  cursor: 'pointer',
                }}
                  onClick={() => navigate(`/tickets/${t.TicketId}`)}
                  onMouseEnter={e => { if (!selectedIds.has(t.TicketId)) e.currentTarget.style.background = C.bg; }}
                  onMouseLeave={e => { if (!selectedIds.has(t.TicketId)) e.currentTarget.style.background = 'transparent'; }}
                >
                  {canBulk && (
                    <td
                      style={{ ...td(), paddingRight: 0 }}
                      onClick={e => { e.stopPropagation(); toggleSelect(t.TicketId); }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(t.TicketId)}
                        onChange={() => toggleSelect(t.TicketId)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                  )}
                  <td style={td()}>
                    <Link to={`/tickets/${t.TicketId}`} style={{ color: C.primary, fontFamily: 'monospace', textDecoration: 'none', fontWeight: 600 }}>
                      {t.TicketKey ?? `#${t.TicketId}`}
                    </Link>
                  </td>
                  <td style={td()}>
                    <Link to={`/tickets/${t.TicketId}`} style={{ color: C.text, textDecoration: 'none' }}>
                      {t.Subject}
                    </Link>
                  </td>
                  <td style={td()}><Badge color={PRIORITY_COLORS[t.Priority] ?? C.secondary}>{t.Priority}</Badge></td>
                  <td style={td()}><Badge color={C.secondary}>{t.StatusName}</Badge></td>
                  <td style={td()} onClick={e => e.stopPropagation()}><UserAccessPopover userId={t.CreatedByUserId} name={t.CreatedByName} /></td>
                  <td style={td()} onClick={e => e.stopPropagation()}>{t.AssignedToName != null
                    ? <UserAccessPopover userId={t.AssignedToUserId!} name={t.AssignedToName} />
                    : <span style={{ color: C.textMuted, fontStyle: 'italic' }}>—</span>}</td>
                  <td style={{ ...td(), color: C.textMuted }}>{fmtRelative(t.CreatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const th = (): React.CSSProperties => ({
  padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px',
});
const td = (): React.CSSProperties => ({ padding: '10px 12px', verticalAlign: 'middle' });

// ============================================================================
// Bulk-bar helpers
// ============================================================================
const bulkBtnSty = (): React.CSSProperties => ({
  background: 'white', color: C.primary,
  border: 'none', padding: '6px 14px', borderRadius: 4,
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
});

const BulkMenu: React.FC<{ children: React.ReactNode; onClose: () => void }> = ({ children }) => (
  <div style={{
    position: 'absolute', top: '100%', right: 0, marginTop: 6,
    background: 'white', border: `1px solid ${C.border}`, borderRadius: 6,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 200, zIndex: 10,
    maxHeight: 320, overflowY: 'auto',
  }}>
    {children}
  </div>
);

const BulkMenuItem: React.FC<{ children: React.ReactNode; onClick: () => void }> = ({ children, onClick }) => (
  <div
    onClick={onClick}
    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: C.text }}
    onMouseEnter={e => (e.currentTarget.style.background = C.bg)}
    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
  >
    {children}
  </div>
);

// ============================================================================
// BulkMergeModal — pick a PARENT ticket to merge the selected ones INTO.
// Server enforces parent != child. Children become Merged status, parent stays.
// ============================================================================
interface SimpleTicket {
  TicketId: number;
  TicketKey?: string;
  Subject: string;
  StatusName: string;
  CreatedByName: string;
}

const BulkMergeModal: React.FC<{
  ticketIds: number[];
  allTickets: any[];
  onClose: () => void;
  onMerged: () => void | Promise<void>;
}> = ({ ticketIds, allTickets, onClose, onMerged }) => {
  const api = useApi();
  const [parentSearch, setParentSearch] = useState('');
  const [parentId, setParentId] = useState<number | null>(null);
  const [merging, setMerging] = useState(false);

  // The pool of possible parents is ALL tickets minus the ones being merged in.
  // Default to non-merged, non-closed parents so we don't accidentally re-open a closed ticket.
  const candidates = React.useMemo(() => {
    const selectedSet = new Set(ticketIds);
    const s = parentSearch.trim().toLowerCase();
    return (allTickets as SimpleTicket[])
      .filter(t => !selectedSet.has(t.TicketId))
      .filter(t => t.StatusName !== 'Merged' && t.StatusName !== 'Closed')
      .filter(t => {
        if (!s) return true;
        return (
          t.Subject.toLowerCase().includes(s) ||
          (t.TicketKey ?? '').toLowerCase().includes(s) ||
          (t.CreatedByName ?? '').toLowerCase().includes(s)
        );
      })
      .slice(0, 100);
  }, [allTickets, parentSearch, ticketIds]);

  const selectedTickets = (allTickets as SimpleTicket[]).filter(t => ticketIds.includes(t.TicketId));

  const doMerge = async () => {
    if (parentId === null) return;
    const parent = candidates.find(c => c.TicketId === parentId);
    if (!parent) return;
    if (!window.confirm(
      `Merge ${ticketIds.length} ticket(s) into ${parent.TicketKey}?\n\n` +
      `They'll be set to status "Merged" and linked to ${parent.TicketKey}, which will stay open.\n\n` +
      `This cannot be easily undone.`
    )) return;
    setMerging(true);
    try {
      const result = await api.post<{ merged: number; parentKey: string }>(
        '/api/tickets/bulk/merge',
        { childIds: ticketIds, parentId }
      );
      alert(`Merged ${result.merged} ticket(s) into ${result.parentKey}.`);
      await onMerged();
    } catch (e: any) {
      alert(`Merge failed: ${e?.message ?? 'unknown error'}`);
    } finally {
      setMerging(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 8, width: '100%', maxWidth: 720,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h3 style={{ margin: 0, color: C.primary, fontSize: 16 }}>
            Merge {ticketIds.length} ticket{ticketIds.length === 1 ? '' : 's'} into a parent
          </h3>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
            Pick the parent ticket. Selected tickets will be moved to "Merged" status and pointed at it.
          </div>
        </div>

        {/* Show what's being merged */}
        <div style={{
          padding: '8px 20px', borderBottom: `1px solid ${C.border}`,
          background: C.bg, fontSize: 12, color: C.textMuted,
        }}>
          <strong style={{ color: C.text }}>Merging:</strong>{' '}
          {selectedTickets.slice(0, 5).map(t => t.TicketKey).join(', ')}
          {selectedTickets.length > 5 && ` … +${selectedTickets.length - 5} more`}
        </div>

        {/* Parent search */}
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}` }}>
          <input
            value={parentSearch}
            onChange={e => setParentSearch(e.target.value)}
            placeholder="Search for the parent ticket (subject, ticket #, or requester)…"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 12px', fontSize: 13,
              border: `1px solid ${C.border}`, borderRadius: 4, background: C.bg,
            }}
          />
        </div>

        {/* List of possible parents */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {candidates.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
              No matching tickets.
            </div>
          ) : candidates.map(t => {
            const selected = parentId === t.TicketId;
            return (
              <div
                key={t.TicketId}
                onClick={() => setParentId(t.TicketId)}
                style={{
                  padding: '10px 20px', cursor: 'pointer',
                  background: selected ? '#eaf2ec' : 'transparent',
                  borderLeft: selected ? `3px solid ${C.primary}` : '3px solid transparent',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
                onMouseEnter={e => { if (!selected) e.currentTarget.style.background = C.bg; }}
                onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
              >
                <input
                  type="radio"
                  name="parent"
                  checked={selected}
                  onChange={() => setParentId(t.TicketId)}
                  style={{ flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.primary, fontWeight: 600 }}>
                      {t.TicketKey}
                    </span>
                    <span style={{ fontSize: 10, color: C.textMuted, padding: '1px 6px', background: C.bg, borderRadius: 3 }}>
                      {t.StatusName}
                    </span>
                  </div>
                  <div style={{
                    fontSize: 13, color: C.text, fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{t.Subject}</div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                    by {t.CreatedByName}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button onClick={onClose} disabled={merging} style={{
            background: 'transparent', border: `1px solid ${C.border}`,
            padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer',
          }}>Cancel</button>
          <button
            onClick={doMerge}
            disabled={parentId === null || merging}
            style={{
              background: parentId === null ? '#ccc' : C.primary, color: 'white',
              border: 'none', padding: '8px 18px', borderRadius: 4,
              fontSize: 13, fontWeight: 600,
              cursor: parentId === null ? 'not-allowed' : 'pointer',
            }}
          >
            {merging ? 'Merging…' : `Merge into ${parentId !== null ? candidates.find(c => c.TicketId === parentId)?.TicketKey : '…'}`}
          </button>
        </div>
      </div>
    </div>
  );
};
